/**
 * ShareLink: encode a (replay, optional level) pair into URL query params
 * so a single click can produce a copy-pasteable URL that, when opened,
 * boots straight into a deterministic replay of the run.
 *
 * Wire format (query string on the site root):
 *
 *     ?r=<base64url-bytes>          (required — the replay)
 *     &L=<base64url-bytes>          (optional — full level JSON, for user
 *                                    levels the receiver can't resolve)
 *     &t=<urlencoded-name>          (optional — built-in song name to play
 *                                    instead of the level's default track)
 *     &T=<base64url-bytes>          (optional — full custom Song JSON, for
 *                                    user-authored tracks the receiver
 *                                    won't have in their built-ins)
 *     &z=1                          (optional — set if r/L/T are deflate-raw
 *                                    compressed; absent ⇒ raw text)
 *
 * Compression: we attempt `CompressionStream('deflate-raw')` (Chrome 80+,
 * Safari 16.4+, FF 113+). When unavailable we fall back to plain UTF-8
 * → base64url. Decoder mirrors this; the `z` flag selects the path.
 *
 * Base64url is the unpadded URL-safe variant (RFC 4648 §5) so the value
 * can sit unescaped in a query string.
 */

import { encodeReplay, parseReplay, type Replay } from './Replay.js';
import type { LevelData } from './LevelLoader.js';
import { getSongs, type Song } from '../audio/Music.js';

export interface SharePayload {
  replay: Replay;
  /** Present only when the replay's level is not a built-in (user level
   *  shared by including its JSON inline). */
  level?: LevelData;
  /** Present only when the recorder was using a custom track different
   *  from the level's default song. Built-ins are referenced by name; a
   *  full Song object means the receiver doesn't need to have authored it. */
  track?: Song;
}

// ---- base64url helpers --------------------------------------------------

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(s: string): Uint8Array {
  const norm = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = norm.length % 4 === 0 ? '' : '='.repeat(4 - (norm.length % 4));
  const bin = atob(norm + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---- compression (best-effort) -----------------------------------------

const HAS_COMPRESSION = typeof CompressionStream !== 'undefined'
  && typeof DecompressionStream !== 'undefined';

async function deflateRaw(text: string): Promise<Uint8Array> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('deflate-raw'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

async function inflateRaw(bytes: Uint8Array): Promise<string> {
  // Wrap the bytes in a fresh ArrayBuffer slice to satisfy strict
  // TS lib typings (Blob expects ArrayBuffer, not ArrayBufferLike).
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const stream = new Blob([ab]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  const buf = await new Response(stream).arrayBuffer();
  return new TextDecoder().decode(buf);
}

/** Encode an arbitrary string to a base64url payload.
 *
 *  `mode`:
 *    - 'auto'  → compress only when it shrinks the payload (default)
 *    - 'raw'   → never compress
 *    - 'force' → always compress (used to keep a single `z` flag in sync
 *                across both `r` and `L` when one of them benefits)
 *
 *  Returns the encoded value plus a flag telling the decoder whether
 *  deflate was applied. */
async function encodeText(
  text: string,
  mode: 'auto' | 'raw' | 'force' = 'auto',
): Promise<{ value: string; compressed: boolean }> {
  const raw = new TextEncoder().encode(text);
  if (mode === 'raw' || !HAS_COMPRESSION) {
    return { value: bytesToBase64Url(raw), compressed: false };
  }
  try {
    const z = await deflateRaw(text);
    if (mode === 'force' || z.length < raw.length) {
      return { value: bytesToBase64Url(z), compressed: true };
    }
    return { value: bytesToBase64Url(raw), compressed: false };
  } catch {
    return { value: bytesToBase64Url(raw), compressed: false };
  }
}

async function decodeText(value: string, compressed: boolean): Promise<string> {
  const bytes = base64UrlToBytes(value);
  if (!compressed) return new TextDecoder().decode(bytes);
  if (!HAS_COMPRESSION) throw new Error('Compressed payload but DecompressionStream unavailable');
  return inflateRaw(bytes);
}

// ---- public API --------------------------------------------------------

export interface BuildShareUrlOptions {
  /** Base URL — typically `window.location.origin + window.location.pathname`
   *  (the share link is intentionally site-relative). */
  baseUrl: string;
  replay: Replay;
  /** Pass when the replay refers to a user level the receiver won't have. */
  level?: LevelData;
  /** Pass when the recorder was using a custom track that should play
   *  during the demo instead of the level's default song. */
  track?: Song;
  /** Names of the receiver's built-in songs. Used to decide whether to
   *  emit `t=<name>` (built-in lookup) or `T=<json>` (inline). When
   *  omitted, the track is always inlined. */
  builtinTrackNames?: ReadonlyArray<string>;
}

/** Build a `https://host/path/?r=…[&L=…][&t=…|&T=…][&z=1]` URL ready for clipboard. */
export async function buildShareUrl(opts: BuildShareUrlOptions): Promise<string> {
  const replayText = encodeReplay(opts.replay);
  // Decide whether the track should travel by-name (built-in) or inline.
  // We only need to encode the inline form; by-name is a plain query param.
  let trackByName: string | null = null;
  let trackJson: string | null = null;
  if (opts.track) {
    const builtins = opts.builtinTrackNames ?? [];
    if (builtins.includes(opts.track.name)) {
      trackByName = opts.track.name;
    } else {
      trackJson = JSON.stringify(opts.track);
    }
  }

  // Encode replay first to learn whether compression helps. The single
  // `z` flag must apply to all encoded payloads (`r`, `L`, `T`) so if any
  // benefit from compression we re-encode the rest with the same mode.
  let r = await encodeText(replayText);
  let L: { value: string; compressed: boolean } | null = null;
  let T: { value: string; compressed: boolean } | null = null;
  if (opts.level) L = await encodeText(JSON.stringify(opts.level));
  if (trackJson) T = await encodeText(trackJson);
  const anyCompressed = r.compressed || (L?.compressed ?? false) || (T?.compressed ?? false);
  const allCompressed = r.compressed && (L ? L.compressed : true) && (T ? T.compressed : true);
  if (anyCompressed && !allCompressed) {
    if (!r.compressed) r = await encodeText(replayText, 'force');
    if (L && !L.compressed) L = await encodeText(JSON.stringify(opts.level!), 'force');
    if (T && !T.compressed) T = await encodeText(trackJson!, 'force');
  }

  const params = new URLSearchParams();
  params.set('r', r.value);
  if (L) params.set('L', L.value);
  if (T) params.set('T', T.value);
  if (trackByName) params.set('t', trackByName);
  if (r.compressed) params.set('z', '1');

  // Strip any pre-existing share params from baseUrl to keep links clean
  // when re-sharing inside an already-open share session.
  const u = new URL(opts.baseUrl);
  u.search = '';
  u.hash = '';
  return `${u.toString()}?${params.toString()}`;
}

/** Best-effort parse of a URL/search-string into a SharePayload. Returns
 *  null when no share params are present. Throws on malformed payloads
 *  (caller decides whether to surface or swallow). */
export async function parseShareLink(input: string | URLSearchParams): Promise<SharePayload | null> {
  const params = typeof input === 'string'
    ? new URLSearchParams(input.includes('?') ? input.slice(input.indexOf('?')) : input)
    : input;
  const r = params.get('r');
  if (!r) return null;
  const compressed = params.get('z') === '1';
  const replayText = await decodeText(r, compressed);
  const replay = parseReplay(replayText);
  if (!replay) throw new Error('Share link contains an invalid replay');

  const out: SharePayload = { replay };
  const L = params.get('L');
  if (L) {
    const levelText = await decodeText(L, compressed);
    const level = JSON.parse(levelText) as LevelData;
    out.level = level;
  }
  // Track override: inline JSON (`T`) wins over by-name (`t`) so a
  // self-contained payload always plays even on receivers without the
  // referenced built-in.
  const T = params.get('T');
  if (T) {
    const trackText = await decodeText(T, compressed);
    out.track = JSON.parse(trackText) as Song;
  } else {
    const t = params.get('t');
    if (t) {
      // Resolve a built-in song by name. If we can't find it (older
      // build with a song that's been renamed/removed), silently drop
      // the track override and let the receiver hear the level default.
      const found = getSongs().find((s) => s.name === t);
      if (found) out.track = found;
    }
  }
  return out;
}
